import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable } from 'rxjs';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

export interface Email {
  emailTo: string;
  emailFrom?: string;
  subject: string;
  title: string;
  html: string;
  text: string;
  includeSignature: boolean;
  signatureType: string;
  status?: 'pending' | 'sent' | 'failed' | 'cancelled';
  ready?: boolean;
  asap: boolean;
  cc?: string;
  createdAt?: String;
  createdAtUnix?: number;

  // Links this email back to the CRM customer it was sent about, so it can
  // show up in that customer's activity history.
  kundekortId?: string;

  sendAt?: String;
  sendAtUnix?: number;

  sentAt?: String;
  sentAtUnix?: number;

  attachments?: Attachment[]; // Array of attachment URLs
}

export interface Attachment {
  name?: string,
  url?: string,
  fileName?: string,
  filePath?: string,
  file?: any,
  bytes?: Uint8Array
}

@Injectable({
  providedIn: 'root'
})
export class EmailService {

  constructor(
    private afs: AngularFirestore,
  ) { }


  getEmails$(): Observable<Email[]> {
    return this.afs.collection<Email>('email', ref => ref.orderBy('sendAtUnix', 'desc')).valueChanges({ idField: 'id' });
  }

  getEmailsByKundekortId(kundekortId: string): Observable<Email[]> {
    return this.afs.collection<Email>('email', ref => ref.where('kundekortId', '==', kundekortId)).valueChanges({ idField: 'id' });
  }

  sendEmail(email: Email): Promise<void | string> {

    if (!email.asap) {
      if (email.sendAtUnix && email.sendAtUnix < moment().unix()) {
        return Promise.reject(new Error('Send time must be in the future'));
      }
    }

    const emailToSend: Email = {
      // Whatever actually sends queued mail reads this field unconditionally
      // (confirmed against production: every sent email has it, even as [];
      // every email missing it entirely has failed with "Cannot read
      // properties of undefined (reading 'map')" — 43/43 failures, 0
      // exceptions). Defaulted here so every caller of sendEmail is covered,
      // same fix as cateringService.js's queueEmail on the functions side.
      attachments: [],
      ...email,
      createdAt: moment().format("YYYY-MM-DD HH:mm:ss"), // Set to current time
      createdAtUnix: moment().unix(),
      sendAt: email.asap ? moment().add(10, "minute").format("YYYY-MM-DD HH:mm:ss") : moment.unix(email.sendAtUnix).format("YYYY-MM-DD HH:mm:ss"), // Set to past time to ensure it is sent immediately
      sendAtUnix: email.asap ? moment().add(10, "minute").unix() : email.sendAtUnix, // Set to past time to ensure it is sent immediately
      asap: email.asap,
      status: 'pending', // Optional: you can set a status field if needed
      ready: true
    };
    console.log('Sending email:', emailToSend);
    // return new Promise<void>((resolve, reject) => {reject('Send operation not implemented yet');});

    // add email to Firestore
    // return this.afs.collection<Email>('email').add(emailToSend).then((docRef) => {
    //   console.log('Email added to Firestore:', emailToSend);
    // }).catch(error => {
    //   console.error('Error adding email to Firestore:', error);
    // });
    // Sends mail and returns its ID
    console.log("emailToSend", emailToSend)
    return this.afs.collection<Email>('email').add(emailToSend).then((docRef) => {
      console.log('Email added to Firestore:', emailToSend);
      return docRef.id
    }).catch(error => {
      console.error('Error adding email to Firestore:', error);
      return Promise.reject(error);
    });

  }

  updateEmailSendTime(id: string, newSendtimeUnix: number): Promise<void> {

    return this.afs.collection<Email>('email').doc(id).update(
      {
        sendAt: moment.unix(newSendtimeUnix).format("YYYY-MM-DD HH:mm:ss"),
        sendAtUnix: newSendtimeUnix,
        asap: false, // Set asap to false since we are updating the send time
        status: 'pending' // Reset status to pending
      }
    ).then(() => {
      console.log('Email updated successfully');
    }).catch(error => {
      console.error('Error updating email:', error);
    });
  }

  updateEmail(id: string, email: Email): Promise<void> {
    // console.log('Updating email with ID:', id, 'to:', email);
    // return new Promise((resolve, reject) => {reject('Update operation not implemented yet');});
    console.log('Updating email with ID:', id, 'to:', email);
    return this.afs.collection<Email>('email').doc(id).update(email).then(() => {
      console.log('Email updated successfully');
    }).catch(error => {
      console.error('Error updating email:', error);
    });
  }

  cancelEmail(id: string): Promise<void> {
    // console.log('Cancelling email with ID:', id);
    // return new Promise((resolve, reject) => {reject('Cancel operation not implemented yet');});
    return this.afs.collection<Email>('email').doc(id).update({
      status: 'cancelled',
      ready: false
    }).then(() => {
      console.log('Email cancelled successfully');
    }).catch(error => {
      console.error('Error cancelling email:', error);
    });
  }


  deleteEmail(id: string): Promise<void> {
    // console.log('Deleting email with ID:', id);
    // return new Promise((resolve, reject) => {reject('Delete operation not implemented yet');});
    return this.afs.collection<Email>('email').doc(id).delete().then(() => {
      console.log('Email deleted successfully');
    }).catch(error => {
      console.error('Error deleting email:', error);
    });
  }
}
